import {
  addAlphaToHex,
  createVectorMapLayerGroup,
  defaultArrowLineStyleFn,
  defaultZoneStyleFn,
  emptyZoneLabelFn,
  generateFeatureStyle,
  MAP_PROJECTION_3857,
  MAP_PROJECTION_4326,
  removeAlphaFromHex,
  type VectorLayerZoneStyle,
  type VectorLayerZoneStyleData,
  type VectorMapLayer,
  type VectorMapLayerConfig
} from '@common';
import { findColorByValue, type ColorMap } from '../../colormap';
import { HeightmapCoverageState } from '../enums';
import { LayerKind, type LayerGroupLayer } from '@shared';
import { parcelYearMapInfoSubTitleFn } from '../../map/utils/map.utils';
import { HeightmapLayerType } from '../enums';

export const createHeightmapStyleFn = async (layer: VectorMapLayer, _: number[]) => {
  const result: VectorLayerZoneStyleData[] = [];
  const colorMap = layer.data.colorMap;

  if (colorMap) {
    const defaultColors = colorMap.colors.map((color) => addAlphaToHex(removeAlphaFromHex(color), '33'));

    layer.data.zones.forEach((zone) => {
      const findedColor = findColorByValue(zone.value, colorMap.breaks, defaultColors);

      const styleConfig: Partial<VectorLayerZoneStyle> = {
        color: findedColor,
        strokeColor: 'transparent'
      };

      result.push({
        defaultStyle: generateFeatureStyle(styleConfig)
      });
    });
  }

  return result;
};

export const getCoverageStateByValue = (value: number, breaks: number[]): HeightmapCoverageState => {
  const states = [
    HeightmapCoverageState.UNCOVERED,
    HeightmapCoverageState.PARTIALLY_COVERED,
    HeightmapCoverageState.COVERED
  ];
  for (let i = breaks.length - 1; i >= 0; i--) {
    if (value >= breaks[i]) {
      return states[i];
    }
  }
  return states[0];
};

export const createHeightmapLayerConfig = (
  subLayerMasterData: LayerGroupLayer,
  classification: any,
  isHeightmap = false,
  enabled = true,
  subLayerData: any,
  subLayerColorMap?: ColorMap
): VectorMapLayerConfig => {
  const subLayerType = subLayerColorMap?.id || '';

  const subLayerKind = subLayerMasterData.kind as LayerKind;
  const needLineArrowStyle = [LayerKind.heightMapAssignedLines, LayerKind.heightMapCandidateLines].includes(
    subLayerKind
  );

  return {
    id: subLayerMasterData.id,
    classification: classification,
    type: subLayerType,
    name: subLayerMasterData.name,
    unitType: subLayerMasterData.unitType || undefined,
    unitTypeName: subLayerMasterData.unitTypeName || undefined,
    rawData: subLayerData,
    featureProjection: MAP_PROJECTION_3857,
    dataProjection: MAP_PROJECTION_4326,
    colorMap: subLayerColorMap as any,
    customData: {
      year: subLayerMasterData.year,
      kind: subLayerMasterData.kind,
      features: subLayerData.features.map((feature: any) => feature.properties)
    },
    zoneStyleFn: isHeightmap ? (!needLineArrowStyle ? createHeightmapStyleFn : undefined) : defaultZoneStyleFn,
    lineStyleFn: needLineArrowStyle ? defaultArrowLineStyleFn : undefined,
    zoneStyleConfig: {
      defaultStyle: {
        color: '#adadad'
      }
    },
    zoneLabelFn: emptyZoneLabelFn,
    enabled,
    mapInfo: {
      subtitleFn: parcelYearMapInfoSubTitleFn
    }
  };
};

export const getHeightmapLayerTypesForDownload = () =>
  [HeightmapLayerType.INCLINE_12_PCT, HeightmapLayerType.INCLINE_17_PCT, HeightmapLayerType.INCLINE_CONTOURS].map(
    (type) => ({
      label: `heightmap.layerType.${type}`,
      value: type
    })
  );

export const createParcelLayerGroup = (feature: any, id: string, name: string) => {
  return createVectorMapLayerGroup({
    id,
    name: name,
    mapLayerConfigs: [
      {
        id,
        classification: '',
        type: feature.type,
        name: '',
        featureProjection: MAP_PROJECTION_3857,
        dataProjection: MAP_PROJECTION_4326,
        rawData: feature as any,
        zoneStyleConfig: {
          defaultStyle: {
            color: 'transparent',
            strokeWidth: 3
          }
        }
      }
    ]
  });
};
